// History — the workspace activity log as a horizontal timeline (Nika
// 2026-09-26: "a very beautiful timeline experience that scrolls
// horizontally and shows histories of activities made by everyone"). The
// stage scrolls sideways through DAYS, newest first: one station per day on
// one axis, and under each station a card per person active that day with
// their events, newest first. The rail beside it filters by person and by
// activity type; the header steps the stage a day at a time.
//
// Chrome register: the sheet is flat, the person cards are the branded
// `card` a step above it, no strokes anywhere — the axis is a painted
// hairline, not a border. Every shaping call is functions/history.js; these
// components hold no logic of their own.

// ── The rail ─────────────────────────────────────────────────────────────

// One filter row of the rail: the compact control height, the house hover
// and press washes, the pressed state as the `selected` step (monochrome —
// hue never marks selection). The person and type rows extend it and declare
// their own pressed predicate.
export const HistoryRailRow = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  width: '100%',
  minHeight: 'controlHCompact',
  padding: 'X Z1',
  round: 'radiusPill',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  // The rail already reads at `Z` (AppSidebar); em tokens compound, so the
  // row keeps the rail's size and its count steps down once.
  fontSize: 'A',
  fontWeight: '500',
  color: 'inactive',
  transition: 'Z defaultBezier background, Z defaultBezier color',
  ':hover': { background: 'hover', color: 'title' },
  ':active': { background: 'selected', color: 'title' },
  ':focus-visible': { background: 'selected', color: 'title' },
  '@coarse': { minHeight: 'controlH' }
}

// The rail's count cell: data numerals in the code face, tabular by brand
// default; `caption` because the row under the pointer is a hovered row.
export const HistoryRailCount = {
  tag: 'span',
  flexShrink: '0',
  fontSize: 'Z1',
  fontFamily: 'Code',
  color: 'caption'
}

// "Everyone" — pressed while no person is chosen.
export const HistoryEveryoneRow = {
  extends: 'HistoryRailRow',
  background: (el, s) => (s.root && s.root.historyPerson ? 'transparent' : 'selected'),
  color: (el, s) => (s.root && s.root.historyPerson ? 'inactive' : 'title'),
  fontWeight: (el, s) => (s.root && s.root.historyPerson ? '500' : '700'),
  'aria-pressed': (el, s) => (s.root && s.root.historyPerson ? 'false' : 'true'),
  onClick: (e, el) => {
    e.preventDefault()
    el.call('setHistoryPerson', null)
  },
  Glyph: {
    boxSize: 'A1',
    round: 'radiusPill',
    flexShrink: '0',
    background: 'widgetPlate',
    flow: 'x',
    align: 'center center',
    Icon: { name: 'people', boxSize: 'Z2', color: 'caption' }
  },
  Name: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    text: '{{ workspace.history.rail.everyone | polyglot }}'
  },
  HistoryRailCount: {
    text: (el, s) => String(el.call('historyRows', s.root, { person: null }).length)
  }
}

// One person of the log: photo, name, count. State: `{ _email, _count }`.
export const HistoryPersonRow = {
  extends: 'HistoryRailRow',
  background: (el, s) => (s.root && s.root.historyPerson === s._email ? 'selected' : 'transparent'),
  color: (el, s) => (s.root && s.root.historyPerson === s._email ? 'title' : 'inactive'),
  fontWeight: (el, s) => (s.root && s.root.historyPerson === s._email ? '700' : '500'),
  'aria-pressed': (el, s) => (s.root && s.root.historyPerson === s._email ? 'true' : 'false'),
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('setHistoryPerson', s._email)
  },
  // A background-image circle, not a bare `Avatar` key: that key extends the
  // framework built-in, an <img> that takes no `background` fill.
  Monogram: {
    boxSize: 'A1',
    round: 'radiusPill',
    flexShrink: '0',
    background: (el, s) => el.call('historyPersonAvatar', s._email)
  },
  Name: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => el.call('historyPersonName', s._email)
  },
  HistoryRailCount: { text: (el, s) => String(s._count || 0) }
}

// "All activity" — pressed while no type is chosen.
export const HistoryAllTypesRow = {
  extends: 'HistoryRailRow',
  background: (el, s) => (s.root && s.root.historyType ? 'transparent' : 'selected'),
  color: (el, s) => (s.root && s.root.historyType ? 'inactive' : 'title'),
  fontWeight: (el, s) => (s.root && s.root.historyType ? '500' : '700'),
  'aria-pressed': (el, s) => (s.root && s.root.historyType ? 'false' : 'true'),
  onClick: (e, el) => {
    e.preventDefault()
    el.call('setHistoryType', null)
  },
  Glyph: {
    boxSize: 'A1',
    round: 'radiusPill',
    flexShrink: '0',
    background: 'widgetPlate',
    flow: 'x',
    align: 'center center',
    Icon: { name: 'sfHistory', boxSize: 'Z2', color: 'caption' }
  },
  Name: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    text: '{{ workspace.history.rail.allTypes | polyglot }}'
  },
  HistoryRailCount: {
    text: (el, s) => String(el.call('historyRows', s.root, { type: null }).length)
  }
}

// One activity type of the log: glyph, label, count. State: `{ _type, _count }`.
export const HistoryTypeRow = {
  extends: 'HistoryRailRow',
  background: (el, s) => (s.root && s.root.historyType === s._type ? 'selected' : 'transparent'),
  color: (el, s) => (s.root && s.root.historyType === s._type ? 'title' : 'inactive'),
  fontWeight: (el, s) => (s.root && s.root.historyType === s._type ? '700' : '500'),
  'aria-pressed': (el, s) => (s.root && s.root.historyType === s._type ? 'true' : 'false'),
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('setHistoryType', s._type)
  },
  Glyph: {
    boxSize: 'A1',
    round: 'radiusPill',
    flexShrink: '0',
    background: 'widgetPlate',
    flow: 'x',
    align: 'center center',
    Icon: { name: (el, s) => el.call('historyTypeIcon', s._type), boxSize: 'Z2', color: 'caption' }
  },
  Name: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => el.call('polyglot', el.call('historyTypeLabelKey', s._type))
  },
  HistoryRailCount: { text: (el, s) => String(s._count || 0) }
}

// The rail: ONE card (AppSidebar wraps its children in CommonCard) with the
// two filter lists under their micro-label headings.
export const HistoryRail = {
  flow: 'y',
  gap: 'A',

  People: {
    flow: 'y',
    gap: 'Y',
    // `Z1`, not the brand's `Y1`: inside the `Z` rail that compounds to 11px.
    CaptionTitle: {
      fontSize: 'Z1',
      Text: { text: '{{ workspace.history.rail.people | polyglot }}' }
    },
    HistoryEveryoneRow: {},
    Rows: {
      flow: 'y',
      gap: 'V',
      childExtends: 'HistoryPersonRow',
      childrenAs: 'state',
      children: (el, s) => el.call('historyPeople', s.root)
    }
  },

  Activity: {
    flow: 'y',
    gap: 'Y',
    CaptionTitle: {
      fontSize: 'Z1',
      Text: { text: '{{ workspace.history.rail.activity | polyglot }}' }
    },
    HistoryAllTypesRow: {},
    Rows: {
      flow: 'y',
      gap: 'V',
      childExtends: 'HistoryTypeRow',
      childrenAs: 'state',
      children: (el, s) => el.call('historyTypes', s.root)
    }
  }
}

// ── The header's step buttons ────────────────────────────────────────────

// The icon-only compact action (36px square): steps the stage one day. The
// call site sets `stepDirection` (-1 newer / 1 older), `stepIcon` and
// `stepLabelKey` — plain values, never functions, so they are stored.
export const HistoryStepBtn = {
  extends: 'AdminIconBtn',
  stepDirection: 1,
  stepIcon: 'chevronRight',
  stepLabelKey: 'workspace.history.step.older',
  'aria-label': (el) => el.call('polyglot', el.stepLabelKey),
  onClick: (e, el) => {
    e.preventDefault()
    el.call('scrollHistoryStage', el.stepDirection)
  },
  Icon: { name: (el) => el.parent.stepIcon, boxSize: 'A' }
}

// ── The rail island ──────────────────────────────────────────────────────

// The rail as a floating island (Nika 2026-09-27: "make overflow area full
// width and sidebar floating above and scroll goes behind it"): the kit's
// rail wrapper with its one card inside, moved from the page row into
// AppMain's body and lifted OUT of its flow — absolute at the sheet's edge
// under the page header, a gutter on three sides, over the stage, which
// takes the whole sheet and slides its columns under the island as it
// scrolls (the stage keeps the island's room on the island's side while it
// shows — HistoryStage). The card is its own height and scrolls inside
// itself when the people list runs long; the wrapper around it lets the
// pointer through to the stage. On a phone the kit's stacked posture holds:
// the island is back in the flow, the row above the stage.
//
// A PAGE SIDEBAR (Nika 2026-10-29, "Same rule for all": "They become page
// sidebars with the shared toggle. Their own show/hide controls go." —
// shared functions/pageSidebar.js): the island floats on the side the
// navbar does not take (the right edge beside a left navbar), and its own
// button hides it for /history — the reopen tab at the same edge brings it
// back. The header's toggle and its root key are gone.
export const HistoryRailIsland = {
  extends: 'AppSidebar',
  tag: 'nav',
  id: 'history-rail',
  'aria-label': (el) => el.call('polyglot', 'workspace.history.rail.aria'),
  pageSidebar: true,
  position: 'absolute',
  top: 'Z1',
  left: 'Z1',
  bottom: 'Z1',
  zIndex: '20',
  padding: '0',
  minHeight: '0',
  pointerEvents: 'none',
  // THE MIRROR for a floating island: the same Z1 gutter at the right edge,
  // and none of the column recipe's turned padding or its step back over a
  // sheet gutter (the island is out of the flow). Stacked, it is static.
  '&[data-sidebar-side="right"]': {
    left: 'auto',
    right: 'Z1',
    padding: '0',
    marginLeft: '0',
    '@mobileL': { right: 'auto' },
    '@appMobile': { right: 'auto' }
  },
  '@tabletS': { width: 'F1', minWidth: 'F1' },
  '@mobileL': {
    position: 'static',
    top: 'auto',
    left: 'auto',
    bottom: 'auto',
    zIndex: 'auto',
    pointerEvents: 'auto'
  },
  '@appMobile': {
    position: 'static',
    top: 'auto',
    left: 'auto',
    bottom: 'auto',
    zIndex: 'auto',
    pointerEvents: 'auto'
  },

  // The hide button one Y inside the card's corner (the island has no
  // padding), and live: the island lets the pointer through, the button
  // takes it, like the card.
  SidebarToggle: {
    top: 'Y',
    right: 'Y',
    pointerEvents: 'auto',
    '&[data-side="right"]': { right: 'Y' }
  },

  // The card is the column's sidebar card (shared AppSidebar childExtends
  // AppSidebarCard, Nika 2026-10-30 "A: navbar glass"): the glass and the
  // `float` lift of every sidebar card come from there.
  HistoryRail: {
    flex: '0 1 auto',
    minHeight: '0',
    overflowY: 'auto',
    overscrollBehaviorY: 'contain',
    pointerEvents: 'auto'
  }
}

// ── The stage ────────────────────────────────────────────────────────────

// A quiet line where the list would be: loading, the read failed, nothing.
export const HistoryNotice = {
  tag: 'p',
  margin: '0',
  padding: 'A B',
  fontSize: 'Z2',
  color: 'caption'
}

// One event: a link to where it happened (an inline link, exempt from the
// control heights like any link in running text). The row lifts on the
// `hover` wash under the pointer. State: the audit-log row.
export const HistoryEventCard = {
  extends: 'Link',
  href: (el, s) => el.call('historyEventHref', s) || null,
  flow: 'x',
  align: 'flex-start left',
  gap: 'Z',
  padding: 'Y Z',
  round: 'radiusControl',
  minWidth: '0',
  textDecoration: 'none',
  color: 'title',
  transition: 'Z defaultBezier background',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  onClick: (e, el, s) => {
    // A modified click is the browser's: a new tab or window.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
    e.preventDefault()
    e.stopPropagation()
    el.call('openHistoryEvent', s)
  },

  Glyph: {
    flexShrink: '0',
    boxSize: 'B',
    round: 'radiusPill',
    background: 'widgetPlate',
    flow: 'x',
    align: 'center center',
    Icon: {
      name: (el, s) => el.call('historyTypeIcon', s.activity_type),
      boxSize: 'Z2',
      color: 'caption'
    }
  },

  Copy: {
    flow: 'y',
    gap: 'V',
    minWidth: '0',
    flex: '1 1 auto',

    // Not `Line`: that key is the brand's divider component.
    MetaLine: {
      flow: 'x',
      align: 'baseline space-between',
      gap: 'Z',
      Type: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '500',
        color: 'caption',
        whiteSpace: 'nowrap',
        text: (el, s) => el.call('polyglot', el.call('historyTypeLabelKey', s.activity_type))
      },
      // On the workspace calendar's clock (historyDays stamps its zone on
      // every event) — the one the card's half was chosen by.
      Time: {
        tag: 'span',
        fontSize: 'Y2',
        fontFamily: 'Code',
        color: 'caption',
        whiteSpace: 'nowrap',
        text: (el, s) => el.call('historyTime', s.created_at, s._timeZone)
      }
    },

    // Two lines at most; a long ticket title ends in an ellipsis.
    Title: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.4',
      color: 'title',
      overflow: 'hidden',
      display: '-webkit-box',
      lineClamp: '2',
      overflowWrap: 'anywhere',
      text: (el, s) => el.call('historyEventTitle', s)
    },

    Ref: {
      hide: (el, s) => !el.call('historyEventRef', s),
      tag: 'span',
      fontSize: 'Y2',
      fontFamily: 'Code',
      color: 'caption',
      text: (el, s) => el.call('historyEventRef', s)
    }
  }
}

// One person's events on one day: the branded card a step above the sheet.
// The newest three show; the button at the foot opens the rest. State:
// `{ _groupKey, _email, _count, _score, _events }`.
export const HistoryAuthorGroup = {
  extends: 'CommonCard',
  padding: 'Z1',
  flow: 'y',
  gap: 'Z',
  minWidth: '0',

  Person: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',
    minWidth: '0',
    padding: '0 Y',

    Monogram: {
      boxSize: 'B',
      round: 'radiusPill',
      flexShrink: '0',
      background: (el, s) => el.call('historyPersonAvatar', s._email)
    },
    Copy: {
      flow: 'y',
      gap: '0',
      minWidth: '0',
      // The person is the third heading level: page title, day, person.
      Name: {
        tag: 'h3',
        margin: '0',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => el.call('historyPersonName', s._email)
      },
      Meta: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) =>
          el
            .call(
              'polyglot',
              s._count === 1 ? 'workspace.history.group.statsOne' : 'workspace.history.group.stats'
            )
            .replace('{count}', String(s._count || 0))
            .replace('{score}', String(s._score || 0))
      }
    }
  },

  Events: {
    flow: 'y',
    gap: 'X',
    childExtends: 'HistoryEventCard',
    childrenAs: 'state',
    children: (el, s) => el.call('historyGroupEvents', s)
  },

  MoreBtn: {
    extends: 'AdminActionBtnCompact',
    alignSelf: 'flex-start',
    hide: (el, s) => (s._count || 0) <= 3,
    'aria-expanded': (el, s) =>
      s.root && s.root._historyExpanded && s.root._historyExpanded[s._groupKey] ? 'true' : 'false',
    text: (el, s) =>
      s.root && s.root._historyExpanded && s.root._historyExpanded[s._groupKey]
        ? el.call('polyglot', 'workspace.history.group.less')
        : el
            .call('polyglot', 'workspace.history.group.more')
            .replace('{count}', String(s._count || 0)),
    onClick: (e, el, s) => {
      e.preventDefault()
      el.call('toggleHistoryGroup', s._groupKey)
    }
  }
}

// The day's station: the middle row of its column, where the day's vertical
// hairline crosses the axis. The axis is one painted hairline across the
// whole stage on the station's top edge (each station paints its own width;
// the columns touch, so the segments meet), the dot marks the crossing, and
// the date hangs under the axis as the day's heading (number, month, weekday
// — one h2, read as one date) with the day's count line. The day's segment
// of the density range fills the station's box under its text, so the
// range's baseline is the count line and its peaks reach the axis whatever
// the station's height. It needs no sticky posture any more: the axis row
// never leaves the view, the halves above and below it scroll.
export const HistoryStation = {
  position: 'relative',
  zIndex: '1',
  flow: 'y',
  gap: 'Y',
  paddingBottom: 'Y',
  minWidth: '0',

  HistoryDensity: {},

  Axis: {
    position: 'relative',
    height: 'A',
    // The axis: from the column's left edge (back across the column's `A`
    // inset, under the previous column's right inset) to its right edge,
    // so neighbouring stations meet in one line. On the row's top edge —
    // the station's — so the range under it starts exactly at the line.
    '::before': {
      content: '""',
      position: 'absolute',
      left: '-A',
      right: '0',
      top: '0',
      height: 'hairline',
      background: 'line'
    },
    // The crossing: centred on the day's vertical hairline, which runs on
    // this row's left edge (the column's `A` inset, where HistoryDay paints it).
    Dot: {
      position: 'absolute',
      left: '0',
      top: '0',
      transform: 'translate(-50%, -50%)',
      boxSize: 'Z',
      round: 'radiusPill',
      background: 'title'
    }
  },

  // The heading resets to the station's size: the type letters are em steps
  // and compound on a heading's own size (the day read as a 73px numeral
  // next to a 33px "micro-label", and "25 Sept YESTERDAY" ran into the next
  // column). The day is the poster numeral (`I`, the reference's big years),
  // the month a step above body, the weekday the micro-label. A day the
  // workspace calendar does not work (a weekend, a holiday) reads its numeral
  // a step quieter, in the caption ink — the week's shape at a glance.
  Date: {
    tag: 'h2',
    margin: '0',
    fontSize: 'A',
    paddingLeft: 'Y',
    paddingRight: 'A',
    flow: 'x',
    align: 'baseline left',
    gap: 'Y',
    minWidth: '0',
    Day: {
      tag: 'span',
      fontSize: 'I',
      fontWeight: '700',
      lineHeight: '1',
      color: (el, s) => (s._workday === false ? 'caption' : 'title'),
      text: (el, s) => el.call('historyStation', s._day, s._timeZone).day
    },
    Month: {
      tag: 'span',
      fontSize: 'C',
      fontWeight: '500',
      color: 'caption',
      text: (el, s) => {
        const at = el.call('historyStation', s._day, s._timeZone)
        return at.year ? `${at.month} ${at.year}` : at.month
      }
    },
    // The weekday as a tracked micro-label; today and yesterday say so.
    // `caption`, not `inactive`: the station sits on the sheet, not on a
    // raised card.
    Weekday: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '500',
      fontFamily: 'Code',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'caption',
      text: (el, s) => {
        const at = el.call('historyStation', s._day, s._timeZone)
        return at.relative || at.weekday
      }
    }
  },

  Meta: {
    tag: 'span',
    paddingLeft: 'Y',
    paddingRight: 'A',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) =>
      [
        el.call(
          'historyCountText',
          'workspace.history.count.events',
          'workspace.history.count.eventsOne',
          s._count
        ),
        el.call(
          'historyCountText',
          'workspace.history.count.people',
          'workspace.history.count.peopleOne',
          s._people
        )
      ].join(' · ')
  }
}

// One half of a day column (Nika 2026-10-01: "make top part, first half of
// the day and bottom part second half of the day based on workdays"): the
// first half of the working day above the axis, the second under it. The
// half's range sits next to the axis as a quiet label, and its cards grow
// away from the axis. The upper half is a reversed column on both levels:
// the label sits on the axis side, the cards stack upward from it, and their
// overflow stays reachable (`justify-content: flex-end` would clip it). The
// label and the cards inset from the day's vertical hairline by `Y` and
// leave the column's right `A`. An empty half keeps its label and its share
// of the column — the day grid gives both halves the same height — so a day
// with nothing after the axis shows a quiet band, never a jump. The instances
// in HistoryDay say which half they hold. State: the day.
export const HistoryHalf = {
  position: 'relative',
  zIndex: '1',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Y',
  minHeight: '0',
  minWidth: '0',
  paddingLeft: 'Y',
  paddingRight: 'A',

  HistoryHalfLabel: {},
  HistoryHalfCards: {}
}

// A half's range ('10:00–14:30') in the code face of the card clocks and the
// caption ink; on a day the calendar does not work, in `inactive` — the
// brand's quiet ink that clears 4.5:1 on every surface. State: the day.
export const HistoryHalfLabel = {
  tag: 'span',
  flexShrink: '0',
  fontSize: 'Y2',
  fontFamily: 'Code',
  whiteSpace: 'nowrap',
  color: (el, s) => (s._workday === false ? 'inactive' : 'caption')
}

// A half's cards, one per person, nearest the axis first (historyDays). The
// list scrolls on its own when the cards outgrow the box — the stage's wheel
// handler leaves it alone (`data-history-scroll`) — and the label stays put.
export const HistoryHalfCards = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  flex: '1 1 0',
  minHeight: '0',
  minWidth: '0',
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehaviorY: 'contain',
  'data-history-scroll': 'y',
  childExtends: 'HistoryAuthorGroup',
  childrenAs: 'state'
}

// The day's segment of the range under the axis: a quiet plate cut to the
// shape historyDensity gives it. It fills the station's box from the axis
// (the box's top edge) to the count line, back across the column's `A`
// inset so the segments meet, and paints under the station's text (the
// station is a stacking context; a negative layer sits behind its own
// content and never over a card), never in the pointer's way.
export const HistoryDensity = {
  position: 'absolute',
  zIndex: '-1',
  left: '-A',
  right: '0',
  top: '0',
  bottom: '0',
  background: 'widgetPlate',
  clipPath: (el, s) => s._densityClip || 'none',
  pointerEvents: 'none'
}

// One day column, the full height of the stage: the first half of the
// working day above the axis, the station on the axis, the second half
// under it (the three rows of a grid, the halves sharing the height equally,
// so the station band sits at the middle of every column and the axis reads
// as one line — the middle of the working day, the same split every day, so
// the columns align). The day's vertical hairline runs top to bottom at the
// column's `A` inset — the reference's station lines. State: `{ _day,
// _timeZone, _workday, _firstRange, _secondRange, _count, _people, _groups,
// _firstHalf, _secondHalf, _densityClip }`.
export const HistoryDay = {
  tag: 'article',
  position: 'relative',
  display: 'grid',
  gridTemplateRows: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  minWidth: '0',
  minHeight: '0',
  paddingLeft: 'A',
  '::before': {
    content: '""',
    position: 'absolute',
    left: 'A',
    top: '0',
    bottom: '0',
    width: 'hairline',
    background: 'line'
  },

  // The station comes first in the DOM — the day's h2 before its people's
  // h3s, so the heading order holds — and the grid rows place the halves
  // around it: the first half above, the station, the second half below.
  HistoryStation: { gridRow: '2' },

  // The first half of the working day, above the axis.
  HistoryHalf: {
    gridRow: '1',
    flexDirection: 'column-reverse',
    paddingTop: 'Z',
    paddingBottom: 'Y',
    HistoryHalfLabel: { text: (el, s) => el.call('historyHalfRange', s._firstRange) },
    HistoryHalfCards: { flexDirection: 'column-reverse', children: (el, s) => s._firstHalf }
  },

  // The second half, under it.
  HistoryHalf_1: {
    gridRow: '3',
    paddingTop: 'Y',
    paddingBottom: 'Z',
    HistoryHalfLabel: { text: (el, s) => el.call('historyHalfRange', s._secondRange) },
    HistoryHalfCards: { children: (el, s) => s._secondHalf }
  }
}

// The stage: a sideways scroller of day columns (a column per day, the `G`
// width), each column the full height of the stage, the whole width of the
// sheet — the rail island floats over one edge (the left, or the right
// beside a left navbar), so while the island shows the stage keeps its room
// on that side (the island's width and the gutters) and its scrolled
// columns pass under the island. It pans
// freely — no scroll snapping: the browser re-snaps after every
// programmatic scroll, which threw the wheel pan back to the column it left
// (measured: a 120px pan on a 305px column landed where it started), and
// the reference timelines scroll freely. The stage itself never scrolls
// down: the halves of a column do. Keyboard: the arrow keys step a day (a
// smooth one-column scroll, as do the header's step buttons); a vertical
// wheel over the axis or an empty half pans sideways, over a scrolling half
// it scrolls the cards.
export const HistoryStage = {
  tag: 'section',
  'aria-label': (el) => el.call('polyglot', 'workspace.history.stage.aria'),
  tabindex: '0',
  flex: '1 1 0',
  minHeight: '0',
  minWidth: '0',
  overflowX: 'auto',
  overflowY: 'hidden',
  overscrollBehaviorX: 'contain',
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: 'G',
  gridTemplateRows: '100%',
  alignItems: 'stretch',
  paddingTop: '0',
  paddingBottom: '0',
  // The island's width plus the gutter on each side of it, on the island's
  // side; the stage's own gutter on the other side and while the island is
  // hidden. A phone stacks the island above the stage instead, so the inset
  // is the stage's gutter there whatever the island does. The island's side
  // and the hidden choice are the page sidebar's (shared
  // functions/pageSidebar.js — page chrome outside the island asks for its
  // page): attributes with static rules. An attribute rule outranks a
  // single class's media rule, so each rule restates the narrow gutter.
  paddingRight: 'B',
  paddingLeft: 'G+B',
  'data-sidebar-side': (el, s) => (el.call('pageSidebarRead', s.root, el) || {}).side,
  'data-sidebar-hidden': (el, s) =>
    (el.call('pageSidebarRead', s.root, el) || {}).hidden ? 'true' : null,
  '&[data-sidebar-side="right"]': {
    paddingLeft: 'B',
    paddingRight: 'G+B',
    '@mobileL': { paddingLeft: 'A', paddingRight: 'A' },
    '@appMobile': { paddingLeft: 'A', paddingRight: 'A' }
  },
  '&[data-sidebar-hidden]': {
    paddingLeft: 'B',
    '@mobileL': { paddingLeft: 'A' },
    '@appMobile': { paddingLeft: 'A' }
  },
  '&[data-sidebar-hidden][data-sidebar-side="right"]': {
    paddingRight: 'B',
    '@mobileL': { paddingRight: 'A' },
    '@appMobile': { paddingRight: 'A' }
  },
  boxSizing: 'border-box',
  '@mobileL': { paddingLeft: 'A', paddingRight: 'A' },
  '@appMobile': { paddingLeft: 'A', paddingRight: 'A' },

  childExtends: 'HistoryDay',
  childrenAs: 'state',
  children: (el, s) => el.call('historyDays', s.root),

  onKeydown: (e, el) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    el.call('scrollHistoryStage', e.key === 'ArrowRight' ? 1 : -1)
  },
  onWheel: (e, el) => el.call('panHistoryStage', e)
}
